import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const QuestionnairePage = () => {
  const { questionnaireId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [reponses, setReponses] = useState({});

  useEffect(() => {
    loadQuestionnaire();
  }, [questionnaireId, token]);

  const loadQuestionnaire = async () => {
    try {
      const response = await axios.get(`/questionnaires/${questionnaireId}?token=${token}`);
      setQuestionnaire(response.data);
      
      if (response.data.statut === 'complete') {
        setSubmitted(true);
      }
      
      // Initialiser les réponses
      const initialReponses = {};
      response.data.questions?.forEach(q => {
        initialReponses[q.id] = q.type === 'rating' ? 0 : '';
      });
      setReponses(initialReponses);
    } catch (err) {
      setError(err.response?.data?.detail || 'Questionnaire non trouvé ou lien invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleRating = (questionId, value) => {
    setReponses(prev => ({...prev, [questionId]: value}));
  };

  const handleText = (questionId, value) => {
    setReponses(prev => ({...prev, [questionId]: value}));
  };

  const handleSubmit = async () => {
    // Vérifier que toutes les questions rating ont une réponse
    const unanswered = questionnaire.questions
      .filter(q => q.type === 'rating' && (!reponses[q.id] || reponses[q.id] === 0));
    
    if (unanswered.length > 0) {
      toast.error('Veuillez répondre à toutes les questions');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`/questionnaires/${questionnaireId}/respond?token=${token}`, { reponses });
      setSubmitted(true);
      toast.success('Merci pour votre réponse !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#8DC63F]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Erreur</h2>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Merci !</h2>
            <p className="text-slate-500">
              Votre réponse a bien été enregistrée. 
              Nous vous remercions pour votre participation.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#8DC63F] rounded-full mb-4">
            <Star className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Questionnaire de satisfaction</h1>
          <p className="text-slate-500 mt-2">{questionnaire?.type_label}</p>
        </div>

        {/* Questions */}
        <Card>
          <CardHeader>
            <CardTitle>Vos réponses</CardTitle>
            <CardDescription>
              Merci de prendre quelques minutes pour répondre à ce questionnaire.
              Votre avis nous aide à améliorer nos services.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {questionnaire?.questions?.map((question, index) => (
              <div key={question.id} className="space-y-3">
                <p className="font-medium text-slate-900">
                  {index + 1}. {question.texte}
                  {question.type === 'rating' && <span className="text-red-500">*</span>}
                </p>
                
                {question.type === 'rating' ? (
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map(value => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleRating(question.id, value)}
                        className="focus:outline-none transition-transform hover:scale-110"
                      >
                        <Star 
                          className={`h-10 w-10 transition-colors ${
                            value <= (reponses[question.id] || 0)
                              ? 'text-amber-400 fill-amber-400' 
                              : 'text-slate-200 hover:text-amber-200'
                          }`}
                        />
                      </button>
                    ))}
                    <span className="ml-4 text-sm text-slate-500">
                      {reponses[question.id] > 0 ? `${reponses[question.id]}/5` : 'Non noté'}
                    </span>
                  </div>
                ) : (
                  <Textarea
                    value={reponses[question.id] || ''}
                    onChange={(e) => handleText(question.id, e.target.value)}
                    placeholder="Votre réponse..."
                    rows={4}
                    className="resize-none"
                  />
                )}
              </div>
            ))}

            <div className="pt-4 border-t">
              <Button 
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-[#8DC63F] hover:bg-[#7AB62F] text-white py-6 text-lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Envoyer mes réponses
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-slate-400 mt-8">
          FISL - Formation et Initiation aux Sports de Loisirs
        </p>
      </div>
    </div>
  );
};

export default QuestionnairePage;
